/**
 * Shared slash-command and ``@path`` decoration primitives.
 *
 * The agent input composer renders these as live token highlights as
 * the user types (see :class:`AgentInput`); :class:`UserPrompt` reuses
 * the same primitives so a submitted ``/compact`` or ``@scenes/...``
 * keeps its chip styling in the conversation transcript instead of
 * degrading into bare plaintext. Keeping the regex and the renderer
 * in one place is what makes "what I typed" and "what I see in my
 * bubble" visually identical.
 *
 * Slash commands are inferred structurally (a leading ``/word``) because
 * they are unambiguous. ``@path`` mentions are deliberately NOT inferred
 * from the raw text: a bare ``@word`` is just as likely to be pasted
 * prose (``@Linear`` from a Slack quote) as a real workspace path. Only
 * the spans the caller has *confirmed* are decorated -- a pick from the
 * completion menu in the composer, or a token backed by a
 * ``<context_paths>`` entry in the transcript -- so the emerald
 * "attachment" styling never lands on something the user did not
 * actually attach.
 */

import type * as React from 'react';
import { cn } from '@/lib/utils/cn';

type InputDecorationKind = 'slash' | 'path';

/** A confirmed ``@path`` mention span in some source string. */
export interface MentionRange {
  /** Index of the leading ``@`` in the source string. */
  start: number;
  /** Exclusive end offset of the mention token (one past its last char). */
  end: number;
}

interface InputDecoration {
  /** Inclusive character offset in the source string. */
  start: number;
  /** Exclusive character offset. */
  end: number;
  kind: InputDecorationKind;
}

/**
 * Detect the leading ``/command`` and decorate the supplied confirmed
 * ``@path`` mention spans in ``value``.
 *
 * Decorations are emitted in source order so the caller can walk them
 * once when interleaving plaintext slices with decorated spans.
 *
 * :param value: Source string the decorations index into.
 * :param mentionRanges: Confirmed ``@path`` spans to decorate -- empty by
 *     default so a caller that has no confirmation signal never paints
 *     speculative mention chips.
 * :return: Decorations sorted by start offset.
 */
export function inputTokenDecorations(
  value: string,
  mentionRanges: ReadonlyArray<MentionRange> = []
): InputDecoration[] {
  const decorations: InputDecoration[] = [];

  const slash = value.match(/^(\/[^\s/]+)/);
  if (slash?.[1]) {
    decorations.push({ start: 0, end: slash[1].length, kind: 'slash' });
  }

  for (const range of mentionRanges) {
    if (range.start < 0 || range.end > value.length || range.end - range.start < 2) continue;
    if (value[range.start] !== '@') continue;
    decorations.push({ start: range.start, end: range.end, kind: 'path' });
  }

  decorations.sort((a, b) => a.start - b.start);
  return decorations;
}

/**
 * Strip the trailing punctuation and slashes the resolver also strips so
 * a body token and the ``<context_paths>`` entry it produced compare
 * apples to apples.
 *
 * :param token: Raw mention token (without the leading ``@``).
 * :return: The normalized token core.
 */
export function trimMentionToken(token: string): string {
  return token.replace(/[),.;:]+$/g, '').replace(/\/+$/g, '');
}

/**
 * Whether a body ``@token`` resolves to one of ``contextPaths``.
 *
 * The composer resolves a confirmed mention into an absolute workspace
 * path, so the typed token is always the tail of that path. Matching by
 * suffix lets the transcript bubble recover "which tokens were real" from
 * the ``<context_paths>`` block alone, without re-deriving the cwd.
 *
 * :param rawToken: Raw mention token (without the leading ``@``).
 * :param contextPaths: Confirmed paths carried by the sent message.
 * :return: ``True`` when the token backs one of the context paths.
 */
export function mentionMatchesContext(rawToken: string, contextPaths: ReadonlyArray<string>): boolean {
  const token = trimMentionToken(rawToken);
  if (!token) return false;
  return contextPaths.some((entry) => {
    const path = entry.replace(/\/+$/g, '');
    return path === token || path.endsWith(`/${token}`);
  });
}

/**
 * Recover the confirmed ``@path`` spans in ``body`` from the
 * ``<context_paths>`` entries the message carried.
 *
 * A bubble has no record of which mentions came from the picker, but it
 * does have the exact paths the composer expanded. Any ``@token`` whose
 * resolved path is in that set was confirmed; everything else (a pasted
 * ``@Linear``) stays plain.
 *
 * :param body: Visible message body with the sentinel blocks stripped.
 * :param contextPaths: Confirmed paths parsed from the ``<context_paths>`` block.
 * :return: Confirmed mention spans in source order.
 */
export function confirmedMentionRanges(body: string, contextPaths: ReadonlyArray<string>): MentionRange[] {
  if (contextPaths.length === 0) return [];
  const ranges: MentionRange[] = [];
  // Each ``@token`` that begins at the string start or after whitespace.
  const regex = /(^|\s)@([^\s@]+)/g;
  let match = regex.exec(body);
  while (match) {
    const start = match.index + (match[1]?.length ?? 0);
    const token = match[2] ?? '';
    if (token.length > 0 && mentionMatchesContext(token, contextPaths)) {
      ranges.push({ start, end: start + token.length + 1 });
    }
    match = regex.exec(body);
  }
  return ranges;
}

/**
 * Shift confirmed mention spans across a single contiguous edit that
 * replaced ``value[editStart, editEnd)`` with ``newLength`` characters.
 *
 * A span entirely before the edit is untouched; one entirely after is
 * shifted by the length delta; one the edit reached into is dropped --
 * editing inside a mention de-confirms it, which is exactly what we want
 * when the user backspaces into a picked path.
 *
 * :param ranges: Spans to transform.
 * :param editStart: Edit start offset in the pre-edit string.
 * :param editEnd: Edit end offset in the pre-edit string.
 * :param newLength: Length of the replacement text.
 * :return: The surviving, shifted spans.
 */
export function shiftMentionRanges(
  ranges: ReadonlyArray<MentionRange>,
  editStart: number,
  editEnd: number,
  newLength: number
): MentionRange[] {
  const delta = newLength - (editEnd - editStart);
  const next: MentionRange[] = [];
  for (const range of ranges) {
    if (range.end <= editStart) {
      next.push({ start: range.start, end: range.end });
    } else if (range.start >= editEnd) {
      next.push({ start: range.start + delta, end: range.end + delta });
    }
    // a span overlapping the edit is intentionally dropped
  }
  return next;
}

/**
 * Keep only spans that still describe a well-formed ``@token`` in
 * ``value`` -- a belt-and-suspenders guard so a coarse edit diff can
 * never leave a stale or malformed range pointing at plain text.
 *
 * :param value: Current source string.
 * :param ranges: Candidate spans.
 * :return: The spans that still bracket a valid mention token.
 */
export function validateMentionRanges(value: string, ranges: ReadonlyArray<MentionRange>): MentionRange[] {
  return ranges.filter((range) => {
    if (range.start < 0 || range.end > value.length || range.end - range.start < 2) return false;
    if (value[range.start] !== '@') return false;
    const token = value.slice(range.start + 1, range.end);
    return token.length > 0 && !/[\s@]/.test(token);
  });
}

/**
 * Carry confirmed mention spans from ``previous`` to ``next`` across an
 * arbitrary value change (typing, paste, cut, programmatic replace).
 *
 * The change is treated as one contiguous edit -- the common case for a
 * controlled textarea -- localized via a prefix/suffix diff, then the
 * spans are shifted and re-validated. A paste that merely *contains*
 * ``@Linear`` introduces no new span, so it never becomes a mention.
 *
 * :param previous: Value before the change.
 * :param next: Value after the change.
 * :param ranges: Confirmed spans against ``previous``.
 * :return: Confirmed spans against ``next``.
 */
export function reconcileMentionRanges(
  previous: string,
  next: string,
  ranges: ReadonlyArray<MentionRange>
): MentionRange[] {
  if (previous === next) return validateMentionRanges(next, ranges);
  const max = Math.min(previous.length, next.length);
  let prefix = 0;
  while (prefix < max && previous[prefix] === next[prefix]) prefix += 1;
  let suffix = 0;
  while (suffix < max - prefix && previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]) {
    suffix += 1;
  }
  const editStart = prefix;
  const editEnd = previous.length - suffix;
  const newLength = next.length - suffix - prefix;
  return validateMentionRanges(next, shiftMentionRanges(ranges, editStart, editEnd, newLength));
}

interface DecoratedInputTextProps {
  value: string;
  decorations: InputDecoration[];
}

/**
 * Render ``value`` with decorated spans for each entry in
 * ``decorations``. Plaintext between decorations is preserved verbatim
 * (whitespace and newlines included) so the composer mirror and the
 * user bubble can hand the same output to ``whitespace-pre-wrap``.
 */
export function DecoratedInputText({ value, decorations }: DecoratedInputTextProps): React.ReactElement {
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  decorations.forEach((decoration, index) => {
    if (decoration.start > cursor) {
      nodes.push(value.slice(cursor, decoration.start));
    }
    nodes.push(
      <span
        key={`${decoration.start}-${decoration.end}-${index}`}
        className={cn(
          'rounded-[4px] box-decoration-clone',
          decoration.kind === 'slash'
            ? 'bg-blue-50/80 shadow-[0_0_0_2px_rgba(219,234,254,0.85)] dark:bg-blue-400/10 dark:shadow-[0_0_0_2px_rgba(96,165,250,0.18)]'
            : 'bg-emerald-50/80 shadow-[0_0_0_2px_rgba(209,250,229,0.9)] dark:bg-emerald-400/10 dark:shadow-[0_0_0_2px_rgba(52,211,153,0.2)]'
        )}
      >
        {value.slice(decoration.start, decoration.end)}
      </span>
    );
    cursor = decoration.end;
  });
  if (cursor < value.length) nodes.push(value.slice(cursor));
  return <>{nodes}</>;
}
